import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Building, Plus, ChevronRight, Clock, CheckCircle,
  TrendingUp, Pause, Calendar, DollarSign
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  planning: { label: "Planejamento", icon: Clock, color: "bg-blue-100 text-blue-700" },
  in_progress: { label: "Em andamento", icon: TrendingUp, color: "bg-amber-100 text-amber-700" },
  paused: { label: "Pausado", icon: Pause, color: "bg-neutral-100 text-neutral-700" },
  completed: { label: "Concluído", icon: CheckCircle, color: "bg-emerald-100 text-emerald-700" }
};

export default function ProjectsListPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, [token]);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setProjects(await res.json());
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen-mobile flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  const activeProjects = projects.filter(p => ["planning", "in_progress"].includes(p.status));
  const completedProjects = projects.filter(p => p.status === "completed");

  return (
    <div className="min-h-screen-mobile bg-white pb-nav" data-testid="projects-list-page">
      {/* Header */}
      <header className="sticky-header border-b border-neutral-100">
        <div className="px-5 h-14 flex items-center gap-3">
          <button 
            onClick={() => navigate("/client")}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-semibold text-neutral-900">Minhas Obras</h1>
            <p className="text-xs text-neutral-500">{projects.length} projetos</p>
          </div>
          {user?.role === "client" && (
            <Button
              onClick={() => navigate("/projects/new")}
              className="btn-minimal btn-primary h-9 px-4 text-sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              Nova Obra
            </Button>
          )}
        </div>
      </header>

      <main className="px-5 py-6 space-y-8">
        {/* Active Projects */}
        {activeProjects.length > 0 && (
          <div className="animate-up">
            <h2 className="font-semibold text-neutral-900 mb-4">Em andamento</h2>
            <div className="space-y-3">
              {activeProjects.map((project, index) => (
                <ProjectCard 
                  key={project.project_id} 
                  project={project} 
                  onClick={() => navigate(`/project/${project.project_id}`)}
                  index={index}
                />
              ))}
            </div>
          </div>
        )}

        {/* Completed Projects */}
        {completedProjects.length > 0 && (
          <div className="animate-up">
            <h2 className="font-semibold text-neutral-900 mb-4">Concluídos</h2>
            <div className="space-y-3">
              {completedProjects.map((project, index) => (
                <ProjectCard 
                  key={project.project_id} 
                  project={project} 
                  onClick={() => navigate(`/project/${project.project_id}`)}
                  index={index}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {projects.length === 0 && (
          <div className="card-subtle p-8 text-center animate-up">
            <div className="w-16 h-16 rounded-full bg-neutral-200 flex items-center justify-center mx-auto mb-4">
              <Building className="w-8 h-8 text-neutral-400" />
            </div>
            <h3 className="font-semibold text-neutral-900 mb-2">Nenhuma obra</h3>
            <p className="text-sm text-neutral-500 mb-6">
              Comece seu projeto de construção agora
            </p>
            {user?.role === "client" && (
              <Button
                onClick={() => navigate("/projects/new")}
                className="btn-minimal btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Iniciar Nova Obra
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function ProjectCard({ project, onClick, index }) {
  const status = STATUS_CONFIG[project.status] || STATUS_CONFIG.planning;
  const StatusIcon = status.icon;

  // Calculate progress based on stages if available
  const stages = project.stages || [];
  const completedStages = stages.filter(s => s.status === "approved" || s.status === "completed").length;
  const progress = stages.length > 0 ? Math.round((completedStages / stages.length) * 100) : 0;

  return (
    <button
      onClick={onClick}
      className="card-clean p-4 w-full text-left animate-up"
      style={{ animationDelay: `${index * 50}ms` }}
      data-testid={`project-card-${project.project_id}`}
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="w-12 h-12 rounded-xl bg-neutral-900 flex items-center justify-center flex-shrink-0">
          <Building className="w-6 h-6 text-white" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="font-semibold text-neutral-900 truncate">{project.name}</h3>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.color} flex-shrink-0`}>
              {status.label}
            </span>
          </div>

          <p className="text-sm text-neutral-500 truncate mb-3">
            {project.address?.street || "Endereço não definido"}
          </p>

          {/* Progress Bar */}
          {stages.length > 0 && (
            <div className="mb-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-neutral-500">Progresso</span>
                <span className="font-medium text-neutral-700">{progress}%</span>
              </div>
              <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-neutral-900 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="flex items-center gap-4 text-xs text-neutral-500">
            <div className="flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              <span>R$ {((project.total_budget || 0) / 1000).toFixed(0)}k</span>
            </div>
            {project.expected_end_date && (
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>{new Date(project.expected_end_date).toLocaleDateString("pt-BR", { month: "short", year: "numeric" })}</span>
              </div>
            )}
          </div>
        </div>

        <ChevronRight className="w-5 h-5 text-neutral-300 flex-shrink-0" />
      </div>
    </button>
  );
}
